import type { RtcConfig } from 'node-datachannel';
import type { WebClientServerConfig } from './WebClientServer.js';
import type { ServerAreaFile } from './ServerAreaFile.js';
import type { ServerGameFile } from './ServerGameFile.js';
import type { MasterServer, MasterServerConfig } from './servers/Master.js';
import type { GateServer, GateServerConfig } from './servers/GateServer.js';
import type { RedisConnectionData } from './RedisClientWrapper.js';

export type ServerTypeStrings = 'connector' | 'area' | 'gate' | 'master';

export type GottiServerConfig = {
	type: ServerTypeStrings;
	id: string | number;
	/**
	 * The address OTHER servers use to reach this one — what it is registered
	 * and advertised under (peers connect to `tcp://<host>:<port>`), and the key
	 * `Starter.startAsHost(host)` selects this server's process by.
	 */
	host: string;
	port: number;
	/**
	 * The local interface this server LISTENS on, when that differs from the
	 * address others reach it by — e.g. `'0.0.0.0'` inside a container whose
	 * `host` is a name only the outside world resolves. Never advertised.
	 *
	 * Unset keeps every listener exactly as it was: the zmq publisher and a
	 * connector's client socket bind `host`; the gate's, master's and web
	 * client's HTTP servers listen on all interfaces. Set, it applies to every
	 * listener of the server.
	 */
	bindHost?: string;
	processId?: string | number;
};

export type WebServerConfig = GottiServerConfig & { webPort: number };

export type GottiAreaServerConfig = Omit<GottiServerConfig, 'type'> & {
	connectors?: (string | number)[];
	/** Arbitrary config blob passed through to the area server */
	configData?: any;
	gameId?: string;
	areaFilePath?: string;
	areaFile?: ServerAreaFile;
	/**
	 * When true, this entry declares a DYNAMIC area *type* (template): it is NOT
	 * booted as a static server (no id/host/port needed) — the framework
	 * create-or-joins areas of this type on demand (client `joinArea(type,{key})`,
	 * `getInitialAreas`, or `system.createArea(type,…)`). The areaFile's `type`
	 * is its identity.
	 */
	dynamic?: boolean;
	/** (dynamic) Cap on how many areas of this type each host may run concurrently. */
	maxConcurrent?: number;
	/** (dynamic) Default empty-teardown grace (ms) before an empty area is reaped. */
	emptyTtlMs?: number;
	/** (dynamic) Default room size for areas of this type. */
	maxClients?: number;
	version?: number;
};

/**
 * True when a `servers.areas` entry is a dynamic-area TEMPLATE (an area-type
 * registration) rather than a bootable static server. Templates have no
 * id/host/port — every consumer that boots, addresses, or opens a connection
 * to "the configured areas" MUST filter through this predicate, or it will
 * compose garbage addresses like `tcp://undefined:undefined` (the dev-boot
 * EINVAL crash) or phantom-boot the template (the REGISTER_AREA death spiral).
 */
export const isDynamicAreaTemplate = (c: { dynamic?: boolean } | null | undefined): boolean => !!c?.dynamic;

/**
 * Core scope-membership check shared by every connector<->area fan-out site: does a
 * connector's registered area scope include this area, by exact id OR by its dynamic
 * template TYPE? An empty/undefined scope is UNSCOPED — matches everything (the
 * established additive default: an unscoped connector reaches every area).
 *
 * This is deliberately ONLY the membership core. The two current consumers wrap it with
 * DIFFERENT surrounding checks, and must keep doing so:
 *  - `Starter.canConnectConnectorToArea` (deployer) also honors an area-side `connectors`
 *    pin and `serverOnly`/`globalSingleton` escape hatches — config-level concerns that
 *    live entirely in the boot-time `serverConfig.areas`/`areaTypes` a Starter process
 *    holds locally.
 *  - `Master.handleAreaPrepare` (the remote dynamic-spawn fan-out) has no visibility into
 *    that local config — a remote instance host only ever hands the master an area TYPE +
 *    id, never a `connectors` pin — so it is, and stays, id-or-type only.
 * Do not fold those outer checks together; only this membership test is shared.
 */
export const connectorScopeMatches = (
	scope: (string | number)[] | undefined,
	areaId: string | number,
	areaType?: string | number | null,
): boolean => {
	if (!scope || !scope.length) return true;
	return scope.includes(areaId) || (areaType != null && scope.includes(areaType));
};

export type ConnectorChangePayload = {
	op: 'added' | 'draining' | 'removed';
	connectorId: string | number;
	/** present when op === 'added' — the registration config areas need */
	config?: any;
};

export type GottiConnectorServerConfig = Omit<WebServerConfig, 'type'> & {
	/**
	 * The host CLIENTS are told to connect to (the gate hands it, with
	 * `webPort`, to every player it seats on this connector). Default: `host`.
	 *
	 * A separate field because `host` is also how this connector's PEERS reach
	 * it: give a containerised connector a public DNS name as `host` and the
	 * master/areas would route their zmq traffic out through the public address
	 * (or, in one process, stop recognising it as co-located and leave IPC).
	 * Only a connector has one: it is the only server whose address the
	 * framework hands to clients (they reach the gate by their own configured
	 * `gateServerURL`).
	 */
	publicHost?: string;
	protocolType?: 'websocket' | 'udp' | 'webRtc';
	maxConnections?: number;
	region?: string;
	game?: string;
	area?: string | number;
	areas?: (string | number)[];
	webRtcOptions?: RtcConfig;
};

export type GateServerConstructor = new (config: GateServerConfig) => GateServer;
export type GottiGateServerConfig = Omit<WebServerConfig, 'type'> & {
	path?: string;
	gateConstructor?: GateServerConstructor;
	useSequentialPublicIds?: boolean;
	seatReservationTimeout?: number;
	authenticationTimeout?: number;
	/**
	 * Namespace for the keys this gate's state store writes when `servers.redis`
	 * is configured (default `'gotti:'`). Ignored in the default memory mode.
	 * See `GateServerConfigOptions.redisKeyPrefix`.
	 */
	redisKeyPrefix?: string;
};

export type MasterServerConstructor = new (config: MasterServerConfig) => MasterServer;
export type GottiMasterServerConfig = Omit<GottiServerConfig, 'type'> & {
	webPort?: number;
	disableAdminWebServer?: boolean;
	path?: string;
	masterConstructor?: MasterServerConstructor;
	/**
	 * Namespace for the keys this master's state store writes when
	 * `servers.redis` is configured (default `'gotti:'`). Ignored in the default
	 * memory mode. See `MasterServerConfigOptions.redisKeyPrefix`.
	 */
	redisKeyPrefix?: string;
};

export type GottiGameConfig = {
	configData?: any;
	connectors?: (string | number)[];
	gameFilePath?: string;
	gameFile?: ServerGameFile;
	id: string;
};

export type GottiServersConfig = {
	webClient: WebClientServerConfig;
	master?: GottiMasterServerConfig;
	gate?: GottiGateServerConfig;
	/**
	 * OPTIONAL redis connection for the GATE and the MASTER — a url string or a
	 * `{ host, port, username?, password?, keyPath?, certPath?, caPath? }` blob.
	 *
	 * Purely a storage-LOCATION choice: it moves the gate's session facts and
	 * the master's members spill + destroy intents out of process memory and
	 * into redis. No capability is redis-only, and removing this field returns
	 * the deployment to today's single-process semantics with no change to any
	 * game's code.
	 *
	 * Handed ONLY to the gate and the master (`Starter.startGate` /
	 * `Starter.startMaster`). Area servers and connectors are all-in-memory by
	 * design and are never configured with it.
	 *
	 * It also does not travel to them: registration publishes the SENDER'S OWN
	 * config to its peers, so the credential is stripped at that boundary —
	 * `BaseServer.sharedConfig`, pinned by `BaseServer.credentialLeak.test.ts`.
	 */
	redis?: RedisConnectionData;
	connectors?: GottiConnectorServerConfig[];
	/**
	 * Static areas (booted at startup) AND dynamic area types (entries flagged
	 * `dynamic: true`, create-or-joined on demand with no static server booted).
	 */
	areas?: GottiAreaServerConfig[];
	/**
	 * Filesystem root for the Unified Assets API (`this.$assets`). Threaded to
	 * every area's `ServerProcess` (via `AreaServerConfigOptions.assetsPath`) so
	 * its per-process `FsAssetSource` is rooted here — writes/reads land under
	 * `<path>/server/...` and `<path>/client/...`. Also threaded to
	 * `WebClientServer`, which serves `<path>/client/...` over
	 * `GET /assets/client/<rel>` (never `server/`). Defaults to `<cwd>/assets`
	 * (area processes) / `<publicPath>/../assets` (web client server) when unset.
	 */
	assets?: {
		path: string;
	};
	/**
	 * Host-level totals for dynamic areas on THIS process (whether it hosts the
	 * master or runs as a remote area host). Feeds automatic admission: "can this
	 * server handle another area?" is computed from these + per-type maxConcurrent.
	 */
	dynamicAreaHost?: {
		maxTotalAreas?: number;
		maxTotalClients?: number;
		/** Max time a type's onDrain hook may hold up an area teardown (default 10s). */
		drainTimeoutMs?: number;
	};
	/**
	 * Cluster trust (docs/security.md). Every server-to-server link is CurveZMQ
	 * authenticated and encrypted — always; this only says where the keys come
	 * from.
	 *
	 * - unset: the Starter generates keypairs IN MEMORY for the servers its own
	 *   process runs (zero configuration for a single-process cluster). A peer
	 *   in another process has no knowable key, so a multi-process config fails
	 *   boot naming that server.
	 * - `keysFile`: the file `gotti keys init` writes (cluster id, cluster
	 *   secret, every server's keypair). Each host loads it; a host may carry a
	 *   trimmed copy without the secret keys of servers it does not run. It is a
	 *   SECRET: owner-only permissions are enforced, never commit it.
	 */
	trust?: {
		keysFile?: string;
	};
};
